export type CrtCrumb = { label: string; to: string | null }

function truncate(s: string, max = 40): string {
  const t = s.trim()
  if (t.length <= max) return t
  return `${t.slice(0, max - 1)}…`
}

/** Label for the left badge (current page / leaf). */
export function crtChannelBadgeLabel(crumbs: CrtCrumb[]): string {
  const last = crumbs[crumbs.length - 1]
  return last?.label ?? 'Home'
}

/** Stable CH-style id from path (01–99); home can override with live meta from backdrop. */
export function channelNumForPath(pathname: string, homeChFromMeta: string | null): string {
  const p = pathname.replace(/\/+$/, '') || '/'
  if (p === '/' && homeChFromMeta && homeChFromMeta.trim()) {
    const t = homeChFromMeta.trim()
    if (/^\d+$/.test(t)) return t.padStart(2, '0')
  }
  let h = 2166136261
  for (let i = 0; i < p.length; i++) h = Math.imul(h ^ p.charCodeAt(i), 16777619)
  return String((Math.abs(h) % 98) + 1).padStart(2, '0')
}

/**
 * Path-aware crumbs for badge label and extended chrome title context.
 * `pageTitle` comes from useCrtPageTitle (event/artist name, CMS page title, etc.).
 */
export function buildCrtCrumbs(pathname: string, pageTitle: string | null): CrtCrumb[] {
  const raw = pathname.replace(/\/+$/, '') || '/'
  const leaf = (fallback: string) => truncate((pageTitle && pageTitle.trim()) || fallback)

  const out: CrtCrumb[] = [{ label: 'Home', to: raw === '/' ? null : '/' }]
  if (raw === '/') return out

  if (raw === '/events') {
    out.push({ label: 'Events', to: null })
    return out
  }
  if (raw.startsWith('/events/')) {
    out.push({ label: 'Events', to: '/events' })
    out.push({ label: leaf('Event'), to: null })
    return out
  }
  if (raw === '/releases') {
    out.push({ label: 'Releases', to: null })
    return out
  }
  if (raw === '/artists') {
    out.push({ label: 'Artists', to: null })
    return out
  }
  if (raw.startsWith('/artists/')) {
    out.push({ label: 'Artists', to: '/artists' })
    out.push({ label: leaf('Artist'), to: null })
    return out
  }
  if (raw === '/about') {
    out.push({ label: leaf('Über Uns'), to: null })
    return out
  }
  if (raw === '/faq') {
    out.push({ label: 'FAQ', to: null })
    return out
  }
  if (raw === '/contact') {
    out.push({ label: 'Kontakt', to: null })
    return out
  }
  if (raw === '/impressum') {
    out.push({ label: leaf('Impressum'), to: null })
    return out
  }
  if (raw === '/datenschutz') {
    out.push({ label: leaf('Datenschutz'), to: null })
    return out
  }

  out.push({ label: '…', to: null })
  return out
}
